import {LitElement, type PropertyValues, type TemplateResult} from "lit";
import type {Constructor} from "./lifetime";
import type {Maybe, Nullable} from "../bootstrap/common/types";

export class UnLitElement extends LitElement {
    protected createRenderRoot(): HTMLElement | DocumentFragment {
        return this;
    }
}

export abstract class ConditionalUnLitElement extends UnLitElement {
    protected abstract shouldRender(): boolean;

    protected abstract renderTemplate(): TemplateResult;

    public render(): Nullable<TemplateResult> {
        if (!this.shouldRender()) {
            this.remove();
            return null;
        }
        return this.renderTemplate();
    }
}

export interface Initializable {
    initialized: () => void;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function Initializing<T extends Constructor<UnLitElement>>(Base: T): T & Constructor<Initializable> {
    return class extends Base implements Initializable {

        public initialized(): void {
            this.classList.remove("initializing");
        }
    };
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function AutoInitializing<T extends Constructor<UnLitElement>>(Base: T): T & Constructor<Initializable> {
    return class extends Base implements Initializable {

        protected firstUpdated(_changedProperties: PropertyValues): void {
            super.firstUpdated(_changedProperties);
            this.initialized();
        }

        public initialized(): void {
            this.classList.remove("initializing");
        }
    };
}

export interface HasEopSlots {
    resolveSlot: <R extends HTMLElement>(name: string) => Maybe<R>;
    resolveSlots: <R extends HTMLElement>(name: string) => R[];
}

const EOP_SLOT_ATTRIBUTE = "eop-slot";
const EOP_SLOT_RESOLVED_ATTRIBUTE = "eop-slot-resolved";

// eslint-disable-next-line @typescript-eslint/naming-convention
export function WithEopSlots<T extends Constructor<UnLitElement>>(Base: T): T & Constructor<HasEopSlots> {
    return class extends Base implements HasEopSlots {

        private slots: HTMLElement[];
        private slotsInitialized: boolean = false;

        public connectedCallback(): void {
            super.connectedCallback();
            if (!this.slotsInitialized) {
                this.slots = Array.from(this.querySelectorAll<HTMLElement>(`[${EOP_SLOT_ATTRIBUTE}]`))
                    .filter(this.isDirectChild);
                this.slots.forEach(slot => slot.remove()); // not in DOM anymore, but still in memory, ready to be put into place by rendering
                this.slotsInitialized = true;
            }
        }

        private isDirectChild = (slot: HTMLElement): boolean => slot.parentElement === this;

        public resolveSlot<R extends HTMLElement>(name: string): Maybe<R> {
            const resolvedSlot = Array.from(this.slots)
                .find(slot => slot.getAttribute(EOP_SLOT_ATTRIBUTE) === name) as R;
            if (resolvedSlot) {
                resolvedSlot.setAttribute(EOP_SLOT_RESOLVED_ATTRIBUTE, "true");
            }
            return resolvedSlot;
        }

        public resolveSlots<R extends HTMLElement>(name: string): R[] {
            const resolvedSlots = Array.from(this.slots)
                .filter(slot => slot.getAttribute(EOP_SLOT_ATTRIBUTE) === name)
                .map(slot => slot as R);
            resolvedSlots.forEach(slot => slot.setAttribute(EOP_SLOT_RESOLVED_ATTRIBUTE, "true"));
            return resolvedSlots;
        }
    };
}